import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { formatDate } from "../lib/formatDate";
import { Navbar } from "../components/Navbar";
import { SwapsOfOrganization } from "../components/SwapsTable";
import { useToast } from "../components/ui/use-toast";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/toaster";
import {
  Chain,
  Role,
  useUpdateWhitelistNameMutation,
  useAddWhitelistMutation,
  useCreateUserMutation,
  useGetTransientTopupsQuery,
  useGetTopupHistoryQuery,
  useGetOrganizationQuery,
  useGetUsersOfOrganizationQuery,
  useRemoveUserFromOrganizationMutation,
  useRemoveWhitelistMutation,
  useGetBalanceQuery,
  useAcceptTopupMutation,
  useRejectTopupMutation,
  useRedirectTopupMutation,
  useToggleOrganizationAutoApproveMutation,
  useUpdateEmailForOrganizationMutation,
  useGetAllUsersQuery,
  useAddUserToOrganizationMutation,
  Decision,
  useUpdateAlertForOrganizationMutation,
  useSendTestAlertForOrganizationMutation,
  useCreateAutoSwapMutation,
  useDeleteAutoSwapMutation,
  useGetMintsQuery,
  useGetAutoSwapQuery,
  Mint,
  useExecuteAutoSwapMutation,
  useToggleTakeExtraFeeMutation,
  useZeroOutBalanceMutation,
  useGetEthGasWalletQuery,
} from "../generated/graphql";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
  Sheet,
} from "../components/ui/sheet";
import {
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Table,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { ExternalLink } from "lucide-react";
import { MintSelect } from "../components/ui/MintSelect";
import { formatNumberToString } from "../lib/formatNumbers";

export function Organization() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [userPage, setUserPage] = useState(1);
  const [topupPage, setTopupPage] = useState(1);
  const [username, setUser] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(Role.View);
  const [userToAdd, setUserToAdd] = useState("");
  const [address, setAddress] = useState("");
  const [chain, setChain] = useState(Chain.Btc);
  const [name, setName] = useState("");
  const [decision, setDecision] = useState(Decision.Approved);
  const [addressRedirect, setAddressRedirect] = useState("");
  const [alertSecret, setAlertSecret] = useState("");
  const [fromMint, setFromMint] = useState<Mint | null>(null);
  const [toMint, setToMint] = useState<Mint | null>(null);
  const [whitelistId, setWhitelistId] = useState<number | null>(null);
  const [tradeImmediate, setTradeImmediate] = useState(false);
  const [tradeRollSwap, setTradeRollSwap] = useState(false);

  const [
    { data: organizationData, error: organizationError },
    refetchOrganization,
  ] = useGetOrganizationQuery({
    variables: { organizationId: id ?? "" },
    pause: !id,
  });
  const organization = organizationData?.getOrganization;

  const [{ data: usersData, error: usersError }, refetchUsers] =
    useGetUsersOfOrganizationQuery({
      variables: { organizationId: id ?? "", page: userPage },
      pause: !id,
    });
  const users = usersData?.getUsersOfOrganization;

  const [{ data: allUsersData, error: allUsersError }] = useGetAllUsersQuery(
    {}
  );
  const allUsers = allUsersData?.getAllUsers;

  const [{ data: addUserToOrgData, error: addUserToOrgError }, addUserToOrg] =
    useAddUserToOrganizationMutation();

  const [{ data: topupHistoryData, error: topupHistoryError }] =
    useGetTopupHistoryQuery({
      variables: {
        organizationId: id ?? "",
        page: topupPage,
      },
      requestPolicy: "network-only",
    });
  const topupHistory = topupHistoryData?.getTopupHistory;

  const [{ data: transientTopupsData, error: transientTopupsError }] =
    useGetTransientTopupsQuery({
      variables: { organizationId: id ?? "" },
      pause: !id,
      requestPolicy: "network-only",
    });
  const transientTopups = transientTopupsData?.getTransientTopups;

  const [{ data: balanceData, error: balanceError }] = useGetBalanceQuery({
    variables: { organizationId: id ?? "" },
    pause: !id,
  });
  const balance = balanceData?.getBalance;

  const [{ data: updateEmailData, error: updateEmailError }, updateEmail] =
    useUpdateEmailForOrganizationMutation();

  const [{ data: createUserData, error: createUserError }, createUser] =
    useCreateUserMutation();

  const [{ data: acceptTopupData, error: acceptTopupError }, acceptTopup] =
    useAcceptTopupMutation();

  const [{ data: rejectTopupData, error: rejectTopupError }, rejectTopup] =
    useRejectTopupMutation();

  const [
    {
      data: toggleOrganizationAutoApproveData,
      error: toggleOrganizationAutoApproveError,
    },
    toggleOrganizationAutoApprove,
  ] = useToggleOrganizationAutoApproveMutation();

  const [
    { data: redirectTopupData, error: redirectTopupError },
    redirectTopup,
  ] = useRedirectTopupMutation();

  const [{ data: addWhitelistData, error: addWhitelistError }, addWhitelist] =
    useAddWhitelistMutation();

  const [
    { data: removeWhitelistData, error: removeWhitelistError },
    removeWhitelist,
  ] = useRemoveWhitelistMutation();

  const [
    { data: updateWhitelistNameData, error: updateWhitelistNameError },
    updateWhitelistName,
  ] = useUpdateWhitelistNameMutation();

  const [
    { data: removeUserFromOrgData, error: removeUserFromOrgError },
    removeUserFromOrg,
  ] = useRemoveUserFromOrganizationMutation();

  const [{ data: updateAlertData, error: updateAlertError }, updateAlert] =
    useUpdateAlertForOrganizationMutation();

  const [
    { data: sendTestAlertData, error: sendTestAlertError },
    sendTestAlert,
  ] = useSendTestAlertForOrganizationMutation();

  const [
    { data: createAutoSwapData, error: createAutoSwapError },
    createAutoSwap,
  ] = useCreateAutoSwapMutation();

  const [
    { data: deleteAutoSwapData, error: deleteAutoSwapError },
    deleteAutoSwap,
  ] = useDeleteAutoSwapMutation();
  const [
    { data: executeAutoSwapData, error: executeAutoSwapError },
    executeAutoSwap,
  ] = useExecuteAutoSwapMutation();

  const [{ data: mintsData, error: mintsError }] = useGetMintsQuery();

  const [{ data: autoSwapData, error: autoSwapError }, refetchAutoSwap] =
    useGetAutoSwapQuery({
      variables: { organizationId: id ?? "" },
      pause: !id,
    });

  const [
    { data: toggleTakeExtraFeeData, error: toggleTakeExtraFeeError },
    toggleTakeExtraFee,
  ] = useToggleTakeExtraFeeMutation();

  const [
    { data: zeroOutBalanceData, error: zeroOutBalanceError },
    zeroOutBalance,
  ] = useZeroOutBalanceMutation();

  const [{ data: gasWalletData, error: gasWalletError }] =
    useGetEthGasWalletQuery();

  console.log(organization);

  useEffect(() => {
    if (toggleOrganizationAutoApproveError) {
      toast({
        title: "Error",
        description: toggleOrganizationAutoApproveError.message,
        variant: "destructive",
      });
    }

    if (updateWhitelistNameError) {
      toast({
        title: "Error",
        description: updateWhitelistNameError.message,
        variant: "destructive",
      });
    }

    if (organizationError) {
      toast({
        title: "Error",
        description: organizationError.message,
        variant: "destructive",
      });
    }

    if (topupHistoryError) {
      toast({
        title: "Error",
        description: topupHistoryError.message,
        variant: "destructive",
      });
    }

    if (transientTopupsError) {
      toast({
        title: "Error",
        description: transientTopupsError.message,
        variant: "destructive",
      });
    }

    if (balanceError) {
      toast({
        title: "Error",
        description: balanceError.message,
        variant: "destructive",
      });
    }

    if (createUserError) {
      toast({
        title: "Error",
        description: createUserError.message,
        variant: "destructive",
      });
    }

    if (usersError) {
      toast({
        title: "Error",
        description: usersError.message,
        variant: "destructive",
      });
    }

    if (addWhitelistError) {
      toast({
        title: "Error",
        description: addWhitelistError.message,
        variant: "destructive",
      });
    }

    if (removeWhitelistError) {
      toast({
        title: "Error",
        description: removeWhitelistError.message,
        variant: "destructive",
      });
    }

    if (removeUserFromOrgError) {
      toast({
        title: "Error",
        description: removeUserFromOrgError.message,
        variant: "destructive",
      });
    }

    if (acceptTopupError) {
      toast({
        title: "Error",
        description: acceptTopupError.message,
        variant: "destructive",
      });
    }

    if (rejectTopupError) {
      toast({
        title: "Error",
        description: rejectTopupError.message,
        variant: "destructive",
      });
    }

    if (redirectTopupError) {
      toast({
        title: "Error",
        description: redirectTopupError.message,
        variant: "destructive",
      });
    }

    if (updateEmailError) {
      toast({
        title: "Error",
        description: updateEmailError.message,
        variant: "destructive",
      });
    }

    if (allUsersError) {
      toast({
        title: "Error",
        description: allUsersError.message,
        variant: "destructive",
      });
    }

    if (addUserToOrgError) {
      toast({
        title: "Error",
        description: addUserToOrgError.message,
        variant: "destructive",
      });
    }

    if (updateAlertError) {
      toast({
        title: "Error",
        description: updateAlertError.message,
        variant: "destructive",
      });
    }

    if (sendTestAlertError) {
      toast({
        title: "Error",
        description: sendTestAlertError.message,
        variant: "destructive",
      });
    }

    if (createAutoSwapError) {
      toast({
        title: "Error",
        description: createAutoSwapError.message,
        variant: "destructive",
      });
    }

    if (deleteAutoSwapError) {
      toast({
        title: "Error",
        description: deleteAutoSwapError.message,
        variant: "destructive",
      });
    }

    if (mintsError) {
      toast({
        title: "Error",
        description: mintsError.message,
        variant: "destructive",
      });
    }

    if (autoSwapError) {
      toast({
        title: "Error",
        description: autoSwapError.message,
        variant: "destructive",
      });
    }

    if (executeAutoSwapError) {
      toast({
        title: "Error",
        description: executeAutoSwapError.message,
        variant: "destructive",
      });
    }

    if (toggleTakeExtraFeeError) {
      toast({
        title: "Error",
        description: toggleTakeExtraFeeError.message,
        variant: "destructive",
      });
    }

    if (zeroOutBalanceError) {
      toast({
        title: "Error",
        description: zeroOutBalanceError.message,
        variant: "destructive",
      });
    }

    if (gasWalletError) {
      toast({
        title: "Error",
        description: gasWalletError.message,
        variant: "destructive",
      });
    }
  }, [
    toggleOrganizationAutoApproveError,
    organizationError,
    usersError,
    balanceError,
    toast,
    createUserError,
    addWhitelistError,
    removeWhitelistError,
    removeUserFromOrgError,
    updateWhitelistNameError,
    topupHistoryError,
    transientTopupsError,
    acceptTopupError,
    rejectTopupError,
    redirectTopupError,
    updateEmailError,
    allUsersError,
    addUserToOrgError,
    updateAlertError,
    sendTestAlertError,
    createAutoSwapError,
    deleteAutoSwapError,
    mintsError,
    autoSwapError,
    executeAutoSwapError,
    toggleTakeExtraFeeError,
    zeroOutBalanceError,
    gasWalletError,
  ]);

  useEffect(() => {
    if (toggleOrganizationAutoApproveData) {
      toast({
        title: "Success",
        description: "Auto approve toggled successfully",
        variant: "default",
      });
    }

    if (updateEmailData) {
      toast({
        title: "Success",
        description: "Email updated successfully",
        variant: "default",
      });
    }

    if (createUserData) {
      toast({
        title: "Success",
        description: "User created successfully",
        variant: "default",
      });

      refetchUsers({ requestPolicy: "network-only" });
      setUser("");
      setEmail("");
      setRole(Role.View);
    }

    if (addWhitelistData) {
      toast({
        title: "Success",
        description: "Address added to whitelist successfully",
        variant: "default",
      });

      refetchOrganization({ requestPolicy: "network-only" });
      setAddress("");
      setChain(Chain.Btc);
    }

    if (removeWhitelistData) {
      toast({
        title: "Success",
        description: "Address removed from whitelist successfully",
        variant: "default",
      });

      refetchOrganization({ requestPolicy: "network-only" });
    }

    if (updateWhitelistNameData) {
      toast({
        title: "Success",
        description: "Whitelist name updated successfully",
        variant: "default",
      });

      refetchOrganization({ requestPolicy: "network-only" });
    }

    if (acceptTopupData) {
      toast({
        title: "Success",
        description: "Topup accepted successfully",
        variant: "default",
      });
    }

    if (rejectTopupData) {
      toast({
        title: "Success",
        description: "Topup rejected successfully",
        variant: "default",
      });
    }

    if (redirectTopupData) {
      toast({
        title: "Success",
        description: "Topup redirected successfully",
        variant: "default",
      });
    }

    if (toggleOrganizationAutoApproveData) {
      toast({
        title: "Success",
        description: "Auto approve toggled successfully",
        variant: "default",
      });
    }

    if (removeUserFromOrgData) {
      toast({
        title: "Success",
        description: "User removed from organization successfully",
        variant: "default",
      });

      refetchUsers({ requestPolicy: "network-only" });
    }

    if (addUserToOrgData) {
      toast({
        title: "Success",
        description: "User added to organization successfully",
        variant: "default",
      });
    }

    if (sendTestAlertData) {
      toast({
        title: "Success",
        description: "Test alert sent successfully check discord",
        variant: "default",
      });
    }

    if (updateAlertData) {
      toast({
        title: "Success",
        description: "Alert updated successfully",
        variant: "default",
      });
      refetchOrganization({ requestPolicy: "network-only" });
    }

    if (createAutoSwapData) {
      toast({
        title: "Success",
        description: "Auto swap created successfully",
        variant: "default",
      });
      refetchAutoSwap({ requestPolicy: "network-only" });
    }

    if (deleteAutoSwapData) {
      toast({
        title: "Success",
        description: "Auto swap deleted successfully",
        variant: "default",
      });
      refetchAutoSwap({ requestPolicy: "network-only" });
    }

    if (executeAutoSwapData) {
      toast({
        title: "Success",
        description: "Auto swap executed successfully",
        variant: "default",
      });
      refetchAutoSwap({ requestPolicy: "network-only" });
    }

    if (toggleTakeExtraFeeData) {
      toast({
        title: "Success",
        description: "Take extra fee toggled successfully",
        variant: "default",
      });
      refetchOrganization({ requestPolicy: "network-only" });
    }

    if (zeroOutBalanceData) {
      toast({
        title: "Success",
        description: "Balance zeroed out successfully",
        variant: "default",
      });
      refetchOrganization({ requestPolicy: "network-only" });
    }
  }, [
    refetchUsers,
    toast,
    createUserData,
    addWhitelistData,
    updateWhitelistNameData,
    refetchOrganization,
    removeWhitelistData,
    removeUserFromOrgData,
    acceptTopupData,
    rejectTopupData,
    redirectTopupData,
    toggleOrganizationAutoApproveData,
    toggleOrganizationAutoApproveError,
    updateEmailData,
    addUserToOrgData,
    sendTestAlertData,
    updateAlertData,
    createAutoSwapData,
    deleteAutoSwapData,
    refetchAutoSwap,
    executeAutoSwapData,
    toggleTakeExtraFeeData,
    zeroOutBalanceData,
  ]);

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Navbar />
      <main className="flex flex-1 flex-col gap-2 p-4 md:gap-4 md:p-8">
        <div className="text-xl font-medium">{organization?.name}</div>
        <div className="flex flex-row items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {organization?.email}
          </div>
          <div
            className="text-sm text-muted-foreground"
            onClick={() => {
              if (!id) return;
              toggleTakeExtraFee({ organizationId: id });
            }}
          >
            {organization?.takeExtraFee
              ? "Take extra fee"
              : "Don't take extra fee"}
          </div>
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="bg-black text-white"
                style={{
                  width: "100px",
                }}
              >
                {organization?.email ? "Edit Email" : "Add Email"}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom">
              <SheetHeader>
                <SheetTitle>Edit Organization Email</SheetTitle>
              </SheetHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="address" className="text-right">
                    Organization Email
                  </Label>
                  <Input
                    id="email"
                    placeholder="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    className="col-span-3"
                  />
                </div>
              </div>
              <SheetFooter>
                <SheetClose asChild>
                  <Button
                    type="submit"
                    onClick={async () => {
                      if (!id) return;
                      const res = await updateEmail({
                        organizationId: id,
                        email,
                      });
                      if (res.error) {
                        toast({
                          title: "Error",
                          description: res.error.message,
                          variant: "destructive",
                        });
                      } else {
                        toast({
                          title: "Success",
                          description: "Email updated successfully",
                          variant: "default",
                        });
                        await refetchOrganization({
                          requestPolicy: "network-only",
                        });
                      }
                    }}
                  >
                    Submit
                  </Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>{" "}
          <div className="text-sm text-muted-foreground">
            {organization?.alertSecret}
          </div>
          <Button
            variant="outline"
            className="bg-black text-white"
            style={{
              width: "100px",
            }}
            onClick={async () => {
              if (!id) return;
              await sendTestAlert({
                organizationId: id,
              });
            }}
          >
            Send test alert
          </Button>
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="bg-black text-white"
                style={{
                  width: "100px",
                }}
              >
                Edit Alert
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom">
              <SheetHeader>
                <SheetTitle>Edit Alert Secret</SheetTitle>
              </SheetHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="address" className="text-right">
                    Alert Secret
                  </Label>
                  <Input
                    id="alertSecret"
                    placeholder="alert secret"
                    value={alertSecret}
                    onChange={(e) => {
                      setAlertSecret(e.target.value);
                    }}
                    className="col-span-3"
                  />
                </div>
              </div>
              <SheetFooter>
                <SheetClose asChild>
                  <Button
                    type="submit"
                    onClick={async () => {
                      if (!id) return;
                      const res = await updateAlert({
                        organizationId: id,
                        alertSecret,
                      });
                      if (res.error) {
                        toast({
                          title: "Error",
                          description: res.error.message,
                          variant: "destructive",
                        });
                      } else {
                        toast({
                          title: "Success",
                          description: "Alert secret updated successfully",
                          variant: "default",
                        });
                        await refetchOrganization({
                          requestPolicy: "network-only",
                        });
                        setAlertSecret("");
                      }
                    }}
                  >
                    Submit
                  </Button>
                </SheetClose>
              </SheetFooter>
            </SheetContent>
          </Sheet>{" "}
          {autoSwapData?.getAutoSwap == null && (
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-black text-white"
                  style={{
                    width: "100px",
                  }}
                >
                  Create Auto Swap
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Create auto swap</SheetTitle>
                </SheetHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="address" className="text-right">
                      From Currency
                    </Label>
                    <MintSelect
                      mintOptions={mintsData?.getMints}
                      onSelect={(mint) => {
                        setFromMint(mint);
                      }}
                      selectedMint={fromMint}
                    />
                    <Label htmlFor="address" className="text-right">
                      To Currency
                    </Label>
                    <MintSelect
                      mintOptions={mintsData?.getMints}
                      onSelect={(mint) => {
                        setToMint(mint);
                      }}
                      selectedMint={toMint}
                    />
                    <Select
                      onValueChange={(value) => {
                        setWhitelistId(parseInt(value));
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Select a whitelisted address" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Whitelisted address</SelectLabel>
                          {organization?.whitelist
                            .filter((w) => w.chain === toMint?.chain)
                            .map((w) => (
                              <SelectItem
                                value={w.id.toString()}
                              >{`${w.name} : ${w.address}`}</SelectItem>
                            ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <div>Urgent/no gouda</div>
                    <input
                      type="checkbox"
                      checked={tradeImmediate}
                      onChange={(e) => {
                        setTradeImmediate(e.target.checked);
                      }}
                    />
                    <Label htmlFor="rollSwap" className="text-right">
                      Roll Swap
                    </Label>
                    <input
                      type="checkbox"
                      checked={tradeRollSwap}
                      onChange={(e) => {
                        setTradeRollSwap(e.target.checked);
                      }}
                    />
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button
                      type="submit"
                      onClick={async () => {
                        if (!id) return;
                        if (!fromMint || !toMint || !whitelistId) {
                          toast({
                            title: "Error",
                            description: "Please select mints and whitelist",
                            variant: "destructive",
                          });
                          return;
                        }
                        const res = await createAutoSwap({
                          organizationId: id,
                          fromMintId: fromMint?.id,
                          toMintId: toMint?.id,
                          endAddress: whitelistId,
                          immediate: tradeImmediate,
                          rollSwap: tradeRollSwap,
                        });
                        if (res.error) {
                          toast({
                            title: "Error",
                            description: res.error.message,
                            variant: "destructive",
                          });
                        } else {
                          toast({
                            title: "Success",
                            description: "Alert secret updated successfully",
                            variant: "default",
                          });
                          await refetchAutoSwap({
                            requestPolicy: "network-only",
                          });
                          setFromMint(null);
                          setToMint(null);
                        }
                      }}
                    >
                      Submit
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          )}{" "}
        </div>
        {autoSwapData?.getAutoSwap && (
          <Card className="xl:col-span-2 mt-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="grid gap-2">
                <CardTitle className="text-xl">Next Auto Swap</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-row items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {autoSwapData.getAutoSwap.fromMint.name} to{" "}
                  {autoSwapData.getAutoSwap.toMint.name} at final address of{" "}
                  {autoSwapData.getAutoSwap.finalAddress.address}
                  {". "}
                  Will {autoSwapData.getAutoSwap.immediate ? "" : "not"} execute
                  immediately
                  {autoSwapData.getAutoSwap.rollSwap
                    ? " and roll swap "
                    : " just next swap"}
                </div>
                {autoSwapData.getAutoSwap.immediate === false && (
                  <Button
                    variant="outline"
                    className="bg-black text-white"
                    style={{
                      width: "100px",
                    }}
                    onClick={async () => {
                      if (!id) return;
                      await executeAutoSwap({
                        organizationId: id,
                      });
                    }}
                  >
                    Execute
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="bg-red-600 text-white"
                  style={{
                    width: "100px",
                  }}
                  onClick={async () => {
                    if (!id) return;
                    await deleteAutoSwap({
                      organizationId: id,
                    });
                  }}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        <Card className="xl:col-span-2 mt-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Pending Topup</CardTitle>
            </div>
            <Button
              onClick={() => {
                if (!id) return;
                toggleOrganizationAutoApprove({ organizationId: id });
              }}
            >
              {organization?.autoApprove
                ? "Disable Auto Approve"
                : "Enable Auto Approve"}
            </Button>
          </CardHeader>
          <CardContent>
            {transientTopups?.length === 0 ? (
              <div className="flex justify-center items-center">
                <p className="text-muted-foreground">No pending topups found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead />
                    <TableHead>Amount</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Decision</TableHead>
                    <TableHead>Finalized</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Tx</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transientTopups?.map((b, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button
                              variant="outline"
                              className="bg-black text-white"
                              style={{
                                marginTop: "0px !important",
                              }}
                            >
                              Review
                            </Button>
                          </SheetTrigger>
                          <SheetContent side="bottom">
                            <SheetHeader>
                              <SheetTitle>Review</SheetTitle>
                            </SheetHeader>
                            <div className="grid gap-4 py-4">
                              <div className="grid grid-cols-4 items-center gap-4">
                                <Label
                                  htmlFor="Decision"
                                  className="text-right"
                                >
                                  Decision
                                </Label>
                                <Select
                                  value={decision}
                                  onValueChange={(v) => {
                                    setDecision(v as Decision);
                                  }}
                                >
                                  <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Chain" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value={Decision.Approved}>
                                      Approved
                                    </SelectItem>
                                    <SelectItem value={Decision.Rejected}>
                                      Rejected
                                    </SelectItem>
                                    <SelectItem value={Decision.Redirected}>
                                      Redirect
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {decision === Decision.Redirected && (
                                <div className="grid grid-cols-4 items-center gap-4">
                                  <Label
                                    htmlFor="address"
                                    className="text-right"
                                  >
                                    Address
                                  </Label>
                                  <Input
                                    id="address"
                                    placeholder="address"
                                    value={addressRedirect}
                                    onChange={(e) => {
                                      setAddressRedirect(e.target.value);
                                    }}
                                    className="col-span-3"
                                  />
                                </div>
                              )}
                            </div>
                            <SheetFooter>
                              <SheetClose asChild>
                                <Button
                                  type="submit"
                                  onClick={async () => {
                                    if (!id) return;

                                    if (decision === Decision.Redirected) {
                                      await redirectTopup({
                                        topupId: b.id,
                                        address: addressRedirect,
                                      });
                                    } else if (decision === Decision.Approved) {
                                      await acceptTopup({
                                        topupId: b.id,
                                      });
                                    } else if (decision === Decision.Rejected) {
                                      await rejectTopup({
                                        topupId: b.id,
                                      });
                                    }
                                  }}
                                >
                                  Submit
                                </Button>
                              </SheetClose>
                            </SheetFooter>
                          </SheetContent>
                        </Sheet>{" "}
                      </TableCell>
                      <TableCell>{formatNumberToString(b.amount)}</TableCell>
                      <TableCell>{b.mint?.name}</TableCell>
                      <TableCell>{b.decision}</TableCell>
                      <TableCell>
                        {b.finalizedAt ? formatDate(b.finalizedAt) : "Pending"}
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger className="cursor-default">
                              {b.id.slice(0, 6)}...
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{b.id}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        {b?.mint?.chain === Chain.Eth ? (
                          <a
                            href={`https://etherscan.io/tx/${b.txHash}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button>
                              <ExternalLink />
                            </Button>
                          </a>
                        ) : (
                          <a
                            href={`https://mempool.space/tx/${b.txHash}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Button>
                              <ExternalLink />
                            </Button>
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <Card className="xl:col-span-2 mt-2">
          <CardHeader className="flex flex-row items-center">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Balances</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {balance?.length === 0 ? (
              <div className="flex justify-center items-center">
                <p className="text-muted-foreground">No balances found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Credit</TableHead>
                    <TableHead>CurrencyId</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Zero Out</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {balance?.map((b, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{b.credit}</TableCell>
                      <TableCell>{b.mintId}</TableCell>
                      <TableCell>{b.mint}</TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          className="bg-black text-white"
                          onClick={async () => {
                            if (!id) return;
                            await zeroOutBalance({
                              organizationId: id,
                              mintId: b.mintId,
                            });
                          }}
                        >
                          Zero Out
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <Card className="xl:col-span-2 mt-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Whitelist addresses</CardTitle>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-black text-white"
                  style={{
                    marginTop: "0px !important",
                  }}
                >
                  Add
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Add Address</SheetTitle>
                </SheetHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="address" className="text-right">
                      Address
                    </Label>
                    <Input
                      id="address"
                      placeholder="address"
                      value={address}
                      onChange={(e) => {
                        setAddress(e.target.value);
                      }}
                      className="col-span-3"
                    />
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="Chain" className="text-right">
                      Chain
                    </Label>
                    <Select
                      value={chain}
                      onValueChange={(v) => {
                        setChain(v as Chain);
                      }}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Chain" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Chain.Btc}>BTC</SelectItem>
                        <SelectItem value={Chain.Eth}>ETH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="Name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                      className="col-span-3"
                    />
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button
                      type="submit"
                      onClick={async () => {
                        if (!id) return;

                        await addWhitelist({
                          organizationId: id,
                          chain,
                          address,
                          name,
                        });
                      }}
                    >
                      Save changes
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>id</TableHead>
                  <TableHead>Chain</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organization?.whitelist?.map((whitelist, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{whitelist.id}</TableCell>
                    <TableCell>{whitelist.chain}</TableCell>
                    <TableCell>{whitelist.address}</TableCell>
                    <TableCell>
                      {whitelist.name ? whitelist.name : "N/A"}
                    </TableCell>
                    <TableCell
                      className="text-right"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                      }}
                    >
                      <Sheet>
                        <SheetTrigger asChild>
                          <Button
                            variant="destructive"
                            className="bg-black text-white"
                            size="sm"
                            style={{
                              marginRight: "5px",
                            }}
                          >
                            Edit
                          </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom">
                          <SheetHeader>
                            <SheetTitle>Edit Name</SheetTitle>
                          </SheetHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <Label htmlFor="name" className="text-right">
                                Name
                              </Label>
                              <Input
                                id="name"
                                placeholder="Name"
                                value={name}
                                onChange={(e) => {
                                  setName(e.target.value);
                                }}
                                className="col-span-3"
                              />
                            </div>
                          </div>
                          <SheetFooter>
                            <SheetClose asChild>
                              <Button
                                type="submit"
                                onClick={async () => {
                                  await updateWhitelistName({
                                    id: whitelist.id,
                                    name,
                                  });

                                  setName("");
                                }}
                              >
                                Save changes
                              </Button>
                            </SheetClose>
                          </SheetFooter>
                        </SheetContent>
                      </Sheet>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                          await removeWhitelist({
                            id: whitelist.id,
                            organizationId: id ?? "",
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card className="xl:col-span-2 mt-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Users</CardTitle>
            </div>

            <div className="flex flex-row gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="bg-black text-white">
                    Add
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom">
                  <SheetHeader>
                    <SheetTitle>Add Existing User</SheetTitle>
                  </SheetHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="ExistingUser" className="text-right">
                        Existing User
                      </Label>
                      <Select
                        value={userToAdd}
                        onValueChange={(v) => {
                          setUserToAdd(v);
                        }}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="" />
                        </SelectTrigger>
                        <SelectContent>
                          {allUsers
                            ?.filter(
                              (user) =>
                                !users?.some((u) => u.userId === user.userId)
                            )
                            .map((user, idx) => (
                              <SelectItem value={user.userId}>
                                {user.username}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <div className="grid grid-cols-4 items-center gap-4">
                        <Label htmlFor="Role" className="text-right">
                          Role
                        </Label>
                        <Select
                          value={role}
                          onValueChange={(v) => {
                            setRole(v as Role);
                          }}
                        >
                          <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={Role.Swap}>Swap</SelectItem>
                            <SelectItem value={Role.View}>View</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <SheetFooter>
                    <SheetClose asChild>
                      <Button
                        type="submit"
                        onClick={async () => {
                          if (!id) return;
                          const res = await addUserToOrg({
                            userId: userToAdd,
                            role,
                            organizationId: id,
                          });
                          if (res.data?.addUserToOrganization) {
                            setUserToAdd("");
                            await refetchUsers({
                              requestPolicy: "network-only",
                            });
                          }
                        }}
                      >
                        Save changes
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="bg-black text-white">
                    Create
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom">
                  <SheetHeader>
                    <SheetTitle>Create User</SheetTitle>
                  </SheetHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="username" className="text-right">
                        Username
                      </Label>
                      <Input
                        id="username"
                        placeholder="Username"
                        value={username}
                        onChange={(e) => {
                          setUser(e.target.value);
                        }}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="email" className="text-right">
                        Email
                      </Label>
                      <Input
                        id="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                        }}
                        className="col-span-3"
                      />
                    </div>
                    <div className="grid grid-cols-4 items-center gap-4">
                      <Label htmlFor="Role" className="text-right">
                        Role
                      </Label>
                      <Select
                        value={role}
                        onValueChange={(v) => {
                          setRole(v as Role);
                        }}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={Role.Swap}>Swap</SelectItem>
                          <SelectItem value={Role.View}>View</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <SheetFooter>
                    <SheetClose asChild>
                      <Button
                        type="submit"
                        onClick={async () => {
                          if (!id) return;
                          await createUser({
                            user: {
                              username,
                              email,
                              role,
                              organizationId: id,
                            },
                          });
                        }}
                      >
                        Save changes
                      </Button>
                    </SheetClose>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user, idx) => (
                  <TableRow
                    key={idx}
                    onClick={() => {
                      navigate(`/user/${user.userId}`);
                    }}
                  >
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await removeUserFromOrg({
                            userId: user.userId,
                            organizationId: id ?? "",
                          });
                        }}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setUserPage(userPage - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink>{userPage}</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setUserPage(userPage + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
        <Card className="xl:col-span-2 mt-2 mb-2">
          <CardHeader className="flex flex-row items-center">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Topup</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {topupHistory?.length === 0 ? (
              <div className="flex justify-center items-center">
                <p className="text-muted-foreground">No topups found</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Amount</TableHead>
                      <TableHead>Currency</TableHead>
                      <TableHead>Finalized</TableHead>
                      <TableHead>Decision</TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Tx</TableHead>
                      <TableHead>LpTx</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {topupHistory?.map((b, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatNumberToString(b.amount)}</TableCell>
                        <TableCell>{b.mint?.name}</TableCell>
                        <TableCell>
                          {b.finalizedAt
                            ? formatDate(b.finalizedAt)
                            : "Pending"}
                        </TableCell>
                        <TableCell>{b.decision}</TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="cursor-default">
                                {b.id.slice(0, 6)}...
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{b.id}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          {b?.mint?.chain === Chain.Eth ? (
                            <a
                              href={`https://etherscan.io/tx/${b.txHash}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button>
                                <ExternalLink />
                              </Button>
                            </a>
                          ) : (
                            <a
                              href={`https://mempool.space/tx/${b.txHash}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button>
                                <ExternalLink />
                              </Button>
                            </a>
                          )}
                        </TableCell>
                        <TableCell>
                          {b?.mint?.chain === Chain.Eth ? (
                            <a
                              href={`https://etherscan.io/tx/${b.lpTxHash}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button>
                                <ExternalLink />
                              </Button>
                            </a>
                          ) : (
                            <a
                              href={`https://mempool.space/tx/${b.lpTxHash}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Button>
                                <ExternalLink />
                              </Button>
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => {
                          setTopupPage(topupPage - 1);
                        }}
                      />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink>{topupPage}</PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => {
                          setTopupPage(topupPage + 1);
                        }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </>
            )}
          </CardContent>
        </Card>
        <Card className="xl:col-span-2 mt-2 mb-2">
          <CardHeader className="flex flex-row items-center">
            <div className="grid gap-2">
              <CardTitle className="text-xl">ETH Gas Wallet</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell
                    onClick={() => {
                      if (gasWalletData?.getEthGasWallet) {
                        navigator.clipboard.writeText(
                          gasWalletData.getEthGasWallet
                        );
                        toast({
                          title: "Copied to clipboard",
                          description: gasWalletData.getEthGasWallet,
                        });
                      }
                    }}
                  >
                    {gasWalletData?.getEthGasWallet}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <SwapsOfOrganization organizationId={id ?? ""} />
        <Card className="xl:col-span-2 mt-2 mb-2">
          <CardHeader className="flex flex-row items-center">
            <div className="grid gap-2">
              <CardTitle className="text-xl">Deposit Addresses</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chain</TableHead>
                  <TableHead>Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organization?.depositAddresses?.map((address, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{address.chain}</TableCell>
                    <TableCell
                      onClick={() => {
                        navigator.clipboard.writeText(address.address);
                        toast({
                          title: "Copied to clipboard",
                          description: address.address,
                        });
                      }}
                    >
                      {address.address}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
      <Toaster />
    </div>
  );
}
